import {
  Button,
  Chip,
  ConfirmDialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { ContentSkeleton } from "../lib/closet";
import useApolloErrorHandler from "../lib/handleApolloError";
import isValidPolicyLink from "../lib/isValidPolicyLink";
import Link, { ExternalLink } from "../lib/Link";
import Screen from "../lib/Screen";
import ScreenSection from "../lib/ScreenSection";
import useBoolean from "../lib/useBoolean";
import { useIsAmeelioSupport } from "../SessionBoundary";
import { DeactivateOrganizationDocument } from "./DeactivateOrganization.generated";
import { GetOrganizationSupportDocument } from "./GetOrganizationSupport.generated";

export default function OrganizationScreen() {
  const deactivating = useBoolean();

  const support = useIsAmeelioSupport();

  const { id } = useParams<{ id: string }>();
  if (!id) throw new Error("missing id param");
  const { data, error } = useQuery(GetOrganizationSupportDocument, {
    fetchPolicy: "cache-and-network",
    variables: { organizationId: id },
  });

  const onError = useApolloErrorHandler();
  const [deactivateOrganization] = useMutation(DeactivateOrganizationDocument, {
    onError,
    onCompleted: () => {
      deactivating.disable();
    },
  });

  if (error) throw error;
  if (!data) return <ContentSkeleton />;

  const { memberships, connectedFacilities } = data.organization;

  return (
    <Screen title={data.organization.name}>
      {memberships.length === 0 ? (
        <Chip label="Inactive" color="red" />
      ) : (
        <Button
          disabled={!support}
          variant="outlined"
          color="error"
          onClick={deactivating.enable}
        >
          Deactivate
        </Button>
      )}
      <ScreenSection title="Members">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {memberships.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{m.visitor.email}</TableCell>
                <TableCell>{m.role}</TableCell>
                <TableCell>{m.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScreenSection>
      <ScreenSection title="Partners">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Facility Name</TableCell>
              <TableCell>System</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Policy Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connectedFacilities.map((cf) => (
              <TableRow key={cf.id}>
                <TableCell>{cf.name}</TableCell>
                <TableCell>
                  <Link to={`/toolbox/systems/${cf.system.id}`}>
                    {cf.system.code}
                  </Link>
                </TableCell>
                <TableCell>{cf.state}</TableCell>
                <TableCell>
                  {cf.policyLink && isValidPolicyLink(cf.policyLink) ? (
                    <ExternalLink href={cf.policyLink}>
                      {cf.policyLink}
                    </ExternalLink>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScreenSection>
      {deactivating.current && (
        <ConfirmDialog
          title="Are you sure?"
          description="Deactivating an organization removes all members and cancels their active connections."
          onConfirm={() => {
            deactivateOrganization({
              variables: { input: { organizationId: id } },
            });
          }}
          onCancel={deactivating.disable}
          variant="remove"
          confirmText="Deactivate"
        />
      )}
    </Screen>
  );
}
